import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import RetraitInstantaneTab from '../Employee/RetraitInstantaneTab';
import HistoriqueRetraitsTab from '../Employee/HistoriqueRetraitsTab';
import ProfilEmployeTab from '../Employee/ProfilEmployeTab';

const EmployeDashboard = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    
    // Rendre setActiveTab disponible globalement pour le sidebar
    window.employeSetActiveTab = setActiveTab;
    
    return () => {
      delete window.employeSetActiveTab;
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employe/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        console.error('Erreur lors du chargement du dashboard');
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const tabs = [
    { key: 'dashboard', name: '🏠 Dashboard', component: null },
    { key: 'retrait', name: '💸 Retrait Instantané', component: RetraitInstantaneTab },
    { key: 'historique', name: '📋 Mes Retraits', component: HistoriqueRetraitsTab },
    { key: 'profil', name: '👤 Mon Profil', component: ProfilEmployeTab },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de vos données...</p>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    if (activeTab === 'dashboard') {
      return (
        <div className="space-y-6">
          {/* Alerte si compte inactif */}
          {dashboardData?.statut_compte && dashboardData?.statut_compte !== 'ACTIF' && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              ⚠️ Votre compte est actuellement <strong>{dashboardData.statut_compte}</strong>. 
              Contactez votre RH pour plus d'informations.
            </div>
          )}

          {/* Alerte période de consommation */}
          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded">
            <div className="flex items-center">
              <span className="mr-2">📅</span>
              <div>
                <p className="font-medium">Période de consommation active</p>
                <p className="text-sm">
                  Vous pouvez effectuer des paiements directs à votre wallet pendant la période configurée par votre entreprise. 
                  {/* TODO: Récupérer les vraies dates depuis l'API entreprise */}
                  Paiements instantanés et illimités.
                </p>
              </div>
            </div>
          </div>

          {/* KPIs principaux */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">💰</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Montant Disponible</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatAmount(dashboardData?.montant_disponible)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">📊</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Consommé ce mois</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatAmount(dashboardData?.montant_consomme)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-2xl">💳</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Salaire Net</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatAmount(dashboardData?.salaire_net)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Détails du plafond */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <span className="mr-2">📋</span>
                Détails de votre plafond d'avance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Salaire net mensuel</span>
                    <span className="font-medium">{formatAmount(dashboardData?.salaire_net)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Pourcentage autorisé</span>
                    <span className="font-medium">{dashboardData?.plafond_pourcentage}%</span>
                  </div>
                  <div className="flex justify-between items-center border-t pt-2">
                    <span className="text-gray-900 font-medium">Montant maximum</span>
                    <span className="font-bold text-green-600">
                      {formatAmount(dashboardData?.montant_max_autorise)}
                    </span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Utilisation ce mois</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Utilisé</span>
                      <span>{formatAmount(dashboardData?.montant_consomme)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Disponible</span>
                      <span className="text-green-600 font-medium">
                        {formatAmount(dashboardData?.montant_disponible)}
                      </span>
                    </div>
                    
                    {/* Barre de progression */}
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full"
                          style={{ 
                            width: `${dashboardData?.montant_max_autorise > 0 ? Math.min(100, (dashboardData?.montant_consomme / dashboardData?.montant_max_autorise) * 100) : 0}%` 
                          }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {dashboardData?.montant_max_autorise > 0 ? Math.round((dashboardData?.montant_consomme / dashboardData?.montant_max_autorise) * 100) : 0}% utilisé
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions rapides */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" 
                  onClick={() => setActiveTab('retrait')}
                  data-testid="action-retrait">
              <CardContent className="p-6 text-center">
                <div className="text-3xl mb-2">💸</div>
                <h3 className="font-medium text-gray-900">Retrait Instantané</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Retirer vers Orange Money ou MTN MoMo
                </p>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" 
                  onClick={() => setActiveTab('historique')}
                  data-testid="action-historique">
              <CardContent className="p-6 text-center">
                <div className="text-3xl mb-2">📋</div>
                <h3 className="font-medium text-gray-900">Mes Retraits</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Voir l'historique de mes retraits
                </p>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" 
                  onClick={() => setActiveTab('profil')}
                  data-testid="action-profil">
              <CardContent className="p-6 text-center">
                <div className="text-3xl mb-2">👤</div>
                <h3 className="font-medium text-gray-900">Mon Profil</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Gérer mes informations personnelles
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    const ActiveComponent = tabs.find(tab => tab.key === activeTab)?.component;
    if (ActiveComponent) {
      return <ActiveComponent dashboardData={dashboardData} onRefresh={fetchDashboardData} />;
    }

    return null;
  };

  return (
    <div className="space-y-6">
      {/* En-tête avec navigation */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 overflow-x-auto px-6">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Contenu de l'onglet actif */}
      {renderTabContent()}
    </div>
  );
};

export default EmployeDashboard;